import { type Feature, type LineString } from 'geojson';
import { merge } from 'lodash-es';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { toLineString } from '../composables';

const useShapesStore = defineStore('shapesStore', () => {
    const shapes = ref<Record<string, string>>({});
    const _shapesToHighlight = ref<Set<string>>(new Set());
    const shapesToHighlight = computed(() => Array.from(_shapesToHighlight.value));
    const features = computed<Record<string, Feature<LineString>>>(() =>
        Object.entries(shapes.value).reduce(
            (acc, [key, encodedLineString]) => {
                acc[key] = {
                    type: 'Feature',
                    geometry: toLineString(encodedLineString),
                    id: key,
                    properties: {}
                };
                return acc;
            },
            {} as Record<string, Feature<LineString>>
        )
    );

    function addShapes(newShapes: Record<string, string>) {
        // merge all shapes with existing trip shapes, skipping existing keys
        shapes.value = merge({}, shapes.value, newShapes);
    }

    function getShapeByKey(key: string) {
        return shapes.value[key];
    }

    function getFeatureByKey(key: string): Feature<LineString> | undefined {
        return features.value[key];
    }

    function $reset() {
        shapes.value = {};
    }

    function highlightShapes(shapeKeys: string[]) {
        shapeKeys.forEach(key => _shapesToHighlight.value.add(key));
    }

    function unhighlightShapes(shapeKeys?: string[]) {
        // if no keys provided, clear all highlights
        if (!shapeKeys || shapeKeys.length === 0) {
            _shapesToHighlight.value.clear();
            return;
        }

        shapeKeys.forEach(key => _shapesToHighlight.value.delete(key));
    }

    return {
        shapes,
        features,
        $reset,
        addShapes,
        getShapeByKey,
        getFeatureByKey,
        highlightShapes,
        unhighlightShapes,
        shapesToHighlight
    };
});

export default useShapesStore;
